import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useWasteStore } from "../../store/store";
import { RoutesList } from "@routes/Routes";
import * as Sentry from "@sentry/react";
import {
  BinBagCategory,
  WasteCategoryLabel,
  WasteCategoryOptionType,
  InferredWasteCategoryOptions,
  binBagResponseMap,
  BIN_BAG_DETECTOR_THRESHOLD,
  CLASSIFICATION_CONFIDENCE_THRESHOLD,
} from "../../db/db";
import { WasteCatagoryType } from "@utils/isValidWasteCategoryAndRoute";
import { getParentCategoryLabel } from "@components/pages/waste/wasteDetails/WasteDetails";
import { emptyLocation } from "@store/storeSlices/wasteDetails";

export interface HandlePhotoPayloadType {
  inferImagePayload: {
    image_uri: string;
    filename: string;
  };
  userCategory: WasteCatagoryType;
}

export interface InferImageResponseType {
  classifications: Array<{
    confidence: number;
    label: WasteCategoryLabel;
  }>;
  metadata: {
    latitude: number;
    longitude: number;
    timestamp: string;
  };
  number_of_bin_bags_group: BinBagCategory | null;
}

export interface HandlePhotoDataType {
  isMismatch: boolean;
  sortedClassifications: WasteCategoryOptionType[];
  location: {
    longitude: number;
    latitude: number;
  };
  bin_bag_classification: BinBagCategory | null;
}

const getParentCategoryValue = (label: WasteCategoryOptionType["label"]) => {
  return InferredWasteCategoryOptions.find(
    (classification) =>
      classification.label.toLowerCase() === label.toLowerCase(),
  )?.parentCategoryValue;
};

type ClassificationsType = Array<{
  parentCategoryValue: WasteCatagoryType;
  confidence: number;
}>;

const sortedClassifications = (
  classifications: ClassificationsType,
  isImageMisMatch: boolean,
  userCategory: WasteCatagoryType,
): WasteCategoryOptionType[] => {
  let newClassifications: WasteCatagoryType[] = [userCategory];
  if (classifications && classifications.length > 0) {
    if (isImageMisMatch) {
      // pick the classification with the highest confidence and the user category
      newClassifications = [
        ...classifications
          .sort((a, b) => b.confidence - a.confidence)
          .map((classification) => classification.parentCategoryValue)
          .slice(0, 1),
        userCategory,
      ];
    }
  }
  // return an array with the suggested waste category and the user selected category
  return [
    InferredWasteCategoryOptions.filter(
      (classification) =>
        classification.parentCategoryValue?.toLowerCase() ===
        newClassifications[0]?.toLowerCase(),
    ).map((cat) => ({
      ...cat,
      parentCategoryLabel: getParentCategoryLabel(
        cat?.parentCategoryValue,
      ) as string,
    }))[0],
    InferredWasteCategoryOptions.filter(
      (classification) =>
        classification.parentCategoryValue?.toLowerCase() ===
        newClassifications[1]?.toLowerCase(),
    ).map((cat) => ({
      ...cat,
      parentCategoryLabel: getParentCategoryLabel(
        cat.parentCategoryValue,
      ) as string,
    }))[0],
  ];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handlePhotoResponse = async (payload: HandlePhotoPayloadType) => {
  try {
    const { userCategory, inferImagePayload } = payload;
    const response = await fetch(
      `${
        import.meta.env.MAINSITE_WCC_APIM_BASE_URL
      }/waste-reports/v1/inference/image`,
      {
        method: "POST",
        body: JSON.stringify({
          ...inferImagePayload,
          // these are the default values and the fields are required for the API to work
          bin_bag_detector_threshold: BIN_BAG_DETECTOR_THRESHOLD,
          classification_confidence_threshold:
            CLASSIFICATION_CONFIDENCE_THRESHOLD,
        }),
        headers: {
          "Ocp-Apim-Subscription-Key": `${
            import.meta.env.MAINSITE_REPORTS_API_KEY as string
          }`,
          "content-type": "application/json",
        },
      },
    );

    const data = (await response.json()) as InferImageResponseType;
    let isMismatch = false;
    const parentCategoryValueArray: ClassificationsType = data.classifications
      .map((c) => ({
        parentCategoryValue: getParentCategoryValue(c.label),
        confidence: c.confidence,
      }))
      .filter(
        (classification) => classification.parentCategoryValue !== undefined,
      ) as ClassificationsType;

    if (data && data.classifications && data.classifications.length > 0) {
      if (
        parentCategoryValueArray.filter(
          (classification) =>
            classification.parentCategoryValue?.toLowerCase() ===
            userCategory.toLowerCase(),
        ).length === 0
      ) {
        isMismatch = true;
      }
    }
    return {
      isMismatch,
      sortedClassifications: sortedClassifications(
        parentCategoryValueArray,
        isMismatch,
        userCategory,
      ),
      location: {
        longitude: data.metadata.longitude,
        latitude: data.metadata.latitude,
      },
      bin_bag_classification: data.number_of_bin_bags_group,
    };
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const usePhotoInferredResponse = () => {
  const navigate = useNavigate();
  const {
    updateIsImageMismatch,
    updateInferredWasteCategory,
    updateInferredNumberOfBinBags,
    updateRenderedQuestions,
    updateLocationOfWaste,
  } = useWasteStore();

  return useMutation({
    mutationFn: handlePhotoResponse,
    onSuccess: (data: HandlePhotoDataType) => {
      const { bin_bag_classification } = data;
      updateIsImageMismatch(data.isMismatch);
      if (data.isMismatch) {
        updateInferredWasteCategory(
          data.sortedClassifications.map((c) => c.parentCategoryLabel),
        );
        updateRenderedQuestions([]);
      }
      if (bin_bag_classification) {
        updateInferredNumberOfBinBags(
          binBagResponseMap[
            bin_bag_classification as keyof typeof binBagResponseMap
          ],
        );
      }
      if (data.location.latitude && data.location.longitude) {
        updateLocationOfWaste({
          ...emptyLocation,
          latitude: String(data.location.latitude),
          longitude: String(data.location.longitude),
        });
      }
      navigate(RoutesList.WASTE_DETAILS);
    },
    onError: (error) => {
      Sentry.captureException(error);
      throw new Error((error as Error).message);
    },
  });
};
